import { createSlice } from '@reduxjs/toolkit';

// Manage Side Nav Menu open/close state

type ThemeColor = {
  primary: string;
  primaryRGB: string;
  icoBase: string;
  icoLight: string;
};

type ThemeColors = {
  blue: ThemeColor;
  green: ThemeColor;
  orange: ThemeColor;
  gray: ThemeColor;
  dark: ThemeColor;
};

// Default theme colors
const themeColors: ThemeColors = {
  blue: {
    icoBase: "0, 156, 222",
    icoLight: "51, 175, 228",
    primary: "#009cde",
    primaryRGB: "0, 156, 222",
  },
  dark: {
    icoBase: "42, 45, 50",
    icoLight: "130, 132, 135",
    primary: "#63666a",
    primaryRGB: "99, 102, 106",
  },
  gray: {
    icoBase: "42, 45, 50",
    icoLight: "130, 132, 135",
    primary: "#63666a",
    primaryRGB: "99, 102, 106",
  },
  green: {
    icoBase: "63, 156, 53",
    icoLight: "108, 175, 93",
    primary: "#3f9c35",
    primaryRGB: "63, 156, 53",
  },
  orange: {
    icoBase: "232, 119, 34",
    icoLight: "236, 146, 78",
    primary: "#e87722",
    primaryRGB: "232, 119, 34",
  },
};

// Default theme options
const themeOptionsInitString = localStorage.getItem("themeOptions");
const themeOptionsInit = themeOptionsInitString
  ? JSON.parse(themeOptionsInitString)
  : {
      colors: {
        ...themeColors.blue,
        base: "",
        bgBase: "",
        bgLight: "",
        borderBase: "",
        shadowBase: "",
      },
      customClientLogo: "",
      darkMode: false,
      mode: "blue",
    };
// set Theme colors 
const setThemeColorVars = () => {
  const colorName = themeOptionsInit.darkMode ? "dark" : themeOptionsInit.mode;

  let themeVars:any = {};
  let colorVars:any = {};
  let themeModeCssURL = '/css/cui.theme.light.css';

  // light mode color
  let cuiColorBase = "#000000";
  let cuiColorBgBase = "#fff";  
  let cuiColorBaseRgb = "0, 0, 0"
  let cuiColorBgLight = "#F1F5F9";
  let cuiColorBorderBase = "#F1F1F1";
  let cuiColorShadowBase = "#aaa"; 

  switch (colorName) {
    case "green":        
      colorVars= themeColors.green;
      document.body.classList.remove('dark-mode');
      break;
    case "blue":
      colorVars = themeColors.blue;
      document.body.classList.remove('dark-mode');
      break;
    case "orange":
      colorVars = themeColors.orange;
      document.body.classList.remove('dark-mode');
      break;
    case "gray":
      colorVars = themeColors.gray;
      document.body.classList.remove('dark-mode');
      break;  
    case "dark":
      colorVars = themeColors.dark;
      themeModeCssURL = '/css/cui.theme.dark.css';
      cuiColorBgBase = "#1d1d1d";
      cuiColorBase = "#fff";
      cuiColorBgLight = "#2d2d2d";
      cuiColorBorderBase = "#2d2d2d";
      cuiColorShadowBase = "#111";
      cuiColorBaseRgb = "255, 255, 255"
      document.body.classList.add('dark-mode');
      break;  
    case "custom" :       
      colorVars.primary =  themeOptionsInit.colors.primary ?? themeColors.blue.primary;
      colorVars.primaryRGB = themeOptionsInit.colors.primary ? hexToRgb(themeOptionsInit.colors.primary) : themeColors.blue.primaryRGB;
      colorVars.icoBase = themeOptionsInit.colors.icoBase ? themeOptionsInit.colors.icoBase : themeColors.blue.icoBase;
      colorVars.icoLight = themeOptionsInit.colors.icoLight ? themeOptionsInit.colors.icoLight : themeColors.blue.icoLight;
      cuiColorBase = themeOptionsInit.colors.base ?? cuiColorBase;
      cuiColorBaseRgb = themeOptionsInit.colors.baseRgb ? themeOptionsInit.colors.baseRgb : cuiColorBaseRgb;
      cuiColorBgBase = themeOptionsInit.colors.bgBase ?? cuiColorBgBase;
      cuiColorBgLight = themeOptionsInit.colors.bgLight ?? cuiColorBgLight;
      cuiColorBorderBase = themeOptionsInit.colors.borderBase ?? cuiColorBorderBase;
      cuiColorShadowBase = themeOptionsInit.colors.shadowBase ?? cuiColorShadowBase;  
      document.body.classList.remove('dark-mode');    
      break;  
    default:
      colorVars = themeColors.blue;            
      break;
  }

  // set theme color
  document.documentElement.style.setProperty("--cui-color-primary", colorVars.primary);
  document.documentElement.style.setProperty("--cui-color-primary-rgb", colorVars.primaryRGB);
  document.documentElement.style.setProperty("--cui-color-ico-base", colorVars.icoBase);
  document.documentElement.style.setProperty("--cui-color-ico-light", colorVars.icoLight);

  // set theme mode color
  document.documentElement.style.setProperty("--cui-color-bg-base", cuiColorBgBase);
  document.documentElement.style.setProperty("--cui-color-base-rgb", cuiColorBaseRgb);
  document.documentElement.style.setProperty("--cui-color-base", cuiColorBase);
  document.documentElement.style.setProperty("--cui-color-bg-light", cuiColorBgLight);
  document.documentElement.style.setProperty("--cui-color-border-base", cuiColorBorderBase);
  document.documentElement.style.setProperty("--cui-color-shadow-base", cuiColorShadowBase);
  
  themeVars.darkMode = themeOptionsInit.darkMode;  
  themeVars.mode = themeOptionsInit.mode;
  themeVars.colors = colorVars;
  themeVars.themeModeCssURL = themeModeCssURL;

  return themeVars;
}

// Redux global state
const initialState = {
  colors: [
      { 
        color : themeColors.blue.primary,
        name  : "blue" 
      },
      { 
        color : themeColors.green.primary,
        name  : "green" 
      },      
      { 
        color : themeColors.orange.primary,
        name  : "orange" 
      },
      { 
        color : themeColors.gray.primary,
        name  : "gray" 
      },
      { 
        color : themeColors.dark.primary,
        name  : "dark" 
      }
  ],
  currentTheme: setThemeColorVars(), 
  customColors: {
    base: themeOptionsInit.colors.base ?? "",
    bgBase: themeOptionsInit.colors.bgBase ?? "",
    bgLight: themeOptionsInit.colors.bgLight ?? "",
    borderBase: themeOptionsInit.colors.borderBase ?? "",
    icoBase: themeOptionsInit.colors.icoBase ?? "",
    icoLight: themeOptionsInit.colors.icoLight ?? "",
    primary: themeOptionsInit.colors.primary ?? "",
    shadowBase: themeOptionsInit.colors.shadowBase ?? ""
  },  
  customLogo: themeOptionsInit.customClientLogo,
  darkMode: themeOptionsInit.darkMode
};

// Redux state slice (function that update states)
export const themeOptionsSlice = createSlice({
  initialState,
  name: 'themeOptions',
  reducers: {    
    
    updateCustomThemeColor: (state, action) => {
      const {colorName} = action.payload;
      const {colorValue} = action.payload;
      const colorValueRgb = hexToRgb(colorValue);   
      
      switch (colorName) {
        case "primary":
          document.documentElement.style.setProperty("--cui-color-primary", colorValue);
          document.documentElement.style.setProperty("--cui-color-primary-rgb", colorValueRgb);  
          state.customColors.primary = colorValue;
          break;
        case "icoBase":
          document.documentElement.style.setProperty("--cui-color-ico-base", colorValueRgb);          
          state.customColors.icoBase = hexToRgb(colorValue);
          break;
        case "icoLight":
          document.documentElement.style.setProperty("--cui-color-ico-light", colorValueRgb);
          state.customColors.icoLight = hexToRgb(colorValue);
          break;
        case "base":
          document.documentElement.style.setProperty("--cui-color-base", colorValue);
          document.documentElement.style.setProperty("--cui-color-base-rgb", colorValueRgb);
          state.customColors.base = colorValue;
          break;
        case "bgBase":
          document.documentElement.style.setProperty("--cui-color-bg-base", colorValue);
          state.customColors.bgBase = colorValue;
          break;
        case "bgLight":
          document.documentElement.style.setProperty("--cui-color-bg-light", colorValue);
          state.customColors.bgLight = colorValue;
          break;
        case "borderBase":
          document.documentElement.style.setProperty("--cui-color-border-base", colorValue);
          state.customColors.borderBase = colorValue;
          break;
        case "shadowBase":
          document.documentElement.style.setProperty("--cui-color-shadow-base", colorValue);
          state.customColors.shadowBase = colorValue;
          break;
        default:
          break;
      }       
      themeOptionsInit.colors = state.customColors;
      localStorage.setItem( "themeOptions",  JSON.stringify(themeOptionsInit) );  
    },
    
updateLogo: (state, action) => {
      const logoUrl = action.payload;
      state.customLogo = logoUrl;   
      themeOptionsInit.customClientLogo = logoUrl;
      themeOptionsInit.colors = state.customColors;
      localStorage.setItem( "themeOptions", JSON.stringify(themeOptionsInit) );  
    },

    // updated preset theme colors
updateThemeColor: (state, action) => {            
      const mode = action.payload.themeColorName;      
      const darkModeOn = mode === 'dark';

      themeOptionsInit.colors = state.customColors;
      themeOptionsInit.darkMode = darkModeOn;
      state.darkMode = darkModeOn;      
      themeOptionsInit.mode = mode;
      
      // save nav active state to localStorage
      localStorage.setItem( "themeOptions", JSON.stringify(themeOptionsInit) );  

      // update current theme color
      state.currentTheme = setThemeColorVars();            
    }
  },
});

// helper function

// convert Hex to RGB
function hexToRgb(hex:string) {  
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  let shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function(m:string, r:string, g:string, b:string) {
    return r + r + g + g + b + b;
  });

  let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? 
          `${parseInt(result[1], 16)  }, ${  parseInt(result[2], 16)  }, ${  parseInt(result[3], 16)}`
          : null;
}

// Action creators are generated for each case reducer function
export const { updateThemeColor, updateLogo, updateCustomThemeColor } = themeOptionsSlice.actions;

export default themeOptionsSlice.reducer;